import classNames, { Argument } from "classnames/dedupe";
import React, {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import SwiperCore, {
  A11y,
  Keyboard,
  Navigation,
  Pagination,
  Scrollbar,
  Thumbs,
} from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { NavigationOptions, PaginationOptions } from "swiper/types";
import SwiperClass from "swiper/types/swiper-class";

import { NavArrowNextSmallIcon } from "../../../../icons";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Keyboard, Thumbs]);

/** `preventClicks` props are a hotfix for clickable elements inside swiper */
SwiperCore.defaults.preventClicks = false;
SwiperCore.defaults.preventClicksPropagation = false;

export type CarouselVariant =
  | "simple-carousel"
  | "fullscreen"
  | "products-carousel"
  | "comparison-carousel"
  | "product-card-thumbnails-carousel"
  | "categories-carousel"
  | "edit-product-details-carousel"
  | "product-preview-carousel"
  | "pdp-main-carousel"
  | "product-preview-carousel-two-slides"
  | "product-colors-carousel"
  | "cards-list-carousel"
  | "brands-carousel"
  | "hero-banner-carousel"
  | "hero-banner-carousel-thumbs"
  | "option-summary"
  | "product-image-gallery-carousel";

export interface ICarousel {
  variant: CarouselVariant;
  children: ReactNode[];
  /** Additional slides for thumbs (second) carousel */
  thumbs?: ReactNode[];
  /**
   * The custom set of css classes used to style component
   *
   */
  classes?: {
    /** Styles applied to outer 'a' element. */
    root?: Argument;
    slide?: Argument;
    scrollbar?: Argument;
  };
  onInstance?(carouselInstance: SwiperClass);
  /** Additional params (like, timeout or instance of an additional carousel) */
  params?: {
    mainCarouselParams?: ICarouselParams;
    secondaryCarouselParams?: ICarouselParams;
    mainCarouselInstance?: SwiperClass;
    secondaryCarouselInstance?: SwiperClass;
    [key: string]: unknown;
  };
}

export interface ICarouselParams extends Swiper {
  className?: string;
  "data-testid"?: string;
}

const Carousel: FC<ICarousel> = ({
  children,
  variant,
  classes,
  params,
  onInstance,
}) => {
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const baseConfig = useMemo(
    () => ({
      slidesPerView: "auto" as const,
      className: classNames(classes?.root),
      /** Stops clicks during swipe */
      preventClicks: true,
      navigation: {
        prevEl: ".js-carousel-prev",
        nextEl: ".js-carousel-next",
      },
      // removeClippedSubviews: false,
      centeredSlides: false,
      initialSlide: 0,
    }),
    [classes],
  );

  // eslint-disable-next-line complexity
  const carouselParams: ICarouselParams = useMemo(() => {
    switch (variant) {
      case "simple-carousel":
        return {
          ...baseConfig,
          navigation: false,
          ...(params?.mainCarouselParams || {}),
        };
      case "fullscreen":
        return {
          ...baseConfig,
          pagination: {
            el: ".slide-number",
            type: "fraction",
          },
          slidesPerView: 1,
          "data-testid": "Carousel__fullscreen",
          ...(params?.mainCarouselParams || {}),
        };
      case "comparison-carousel":
        return {
          ...baseConfig,
          scrollbar: {
            draggable: true,
          },
          ...(params?.mainCarouselParams || {}),
        };
      case "product-card-thumbnails-carousel":
        return {
          ...baseConfig,
          spaceBetween: 4,
          className: classNames("product-card__preview-list", classes?.root),
          ...(params?.mainCarouselParams || {}),
        };
      case "categories-carousel":
      case "brands-carousel":
      case "products-carousel":
        return {
          ...baseConfig,
          className: classNames("uds-carousel", classes?.root),
          scrollbar: {
            draggable: true,
          },
          ...(params?.mainCarouselParams || {}),
        };
      case "cards-list-carousel":
        return {
          ...baseConfig,
          className: classNames("banner-container", classes?.root),
          scrollbar: {
            draggable: true,
          },
          preventClicks: true,
          preventClicksPropagation: true,
          ...(params?.mainCarouselParams || {}),
        };
      case "edit-product-details-carousel":
        return {
          ...baseConfig,
          pagination: {
            el: ".slide-number",
            type: "fraction",
          },
          ...(params?.mainCarouselParams || {}),
        };
      case "product-preview-carousel":
        return {
          ...baseConfig,
          preventClicks: true,
          preventClicksPropagation: true,
          navigation: false,
          pagination: {
            el: ".slide-number",
            type: "fraction",
          },
          scrollbar: {
            draggable: false,
            el: ".swiper-scrollbar",
          },
          ...(params?.mainCarouselParams || {}),
        };
      case "pdp-main-carousel":
        return {
          ...baseConfig,
          className: classNames("uds-pdp-main-carousel", classes?.root),
          navigation: false,
          slidesPerView: 1,
          "data-testid": "pdp-main-carousel",
          ...(params?.mainCarouselParams || {}),
        };
      case "product-preview-carousel-two-slides":
        return {
          ...baseConfig,
          navigation: false,
          slidesPerView: 2,
          pagination: {
            el: ".slide-number",
            type: "fraction",
          },
          spaceBetween: 16,
          scrollbar: {
            draggable: false,
            el: ".swiper-scrollbar",
          },
          ...(params?.mainCarouselParams || {}),
        };
      case "product-colors-carousel":
        return {
          ...baseConfig,
          className: classNames("product-card__preview-list", classes?.root),
          ...(params?.mainCarouselParams || {}),
        };
      case "product-image-gallery-carousel":
        return {
          ...baseConfig,
          direction: "vertical",
          className: classNames("product-card__preview-list", classes?.root),
          "data-testid": "product-image-gallery-carousel",
          ...(params?.mainCarouselParams || {}),
        };
      case "hero-banner-carousel":
        return {
          ...baseConfig,
          slidesPerView: 1,
          className: classNames("uds-carousel-hero", classes?.root),
          navigation: {
            nextEl: ".uds-carousel-hero__next",
            prevEl: ".uds-carousel-hero__prev",
          },
          autoplay: {
            delay: 6000,
          },
          loop: true,
          pagination: false,
          spaceBetween: 16,
          keyboard: {
            enabled: true,
          },
          thumbs: {
            swiper: params?.secondaryCarouselInstance,
          },
          ...(params?.mainCarouselParams || {}),
        };
      case "hero-banner-carousel-thumbs":
        return {
          ...baseConfig,
          className: classNames("uds-carousel-hero__thumbs", classes?.root),
          allowTouchMove: true,
          navigation: false,
          pagination: false,
          slideToClickedSlide: true,
          spaceBetween: 10,
          breakpoints: {
            // md
            768: {
              spaceBetween: 16,
            },
            // lg
            1024: {
              allowTouchMove: false,
              spaceBetween: 32,
            },
          },
          "data-testid": "Carousel__thumbs",
          onSlideChange({ realIndex }) {
            if (params.mainCarouselInstance?.realIndex !== realIndex) {
              params.mainCarouselInstance?.slideToLoop?.(realIndex);
            }
          },
          ...(params?.secondaryCarouselParams || {}),
        };

      default:
        return {};
    }
  }, [baseConfig, classes, params, variant]);

  const slideCommonClasses = useMemo(
    () =>
      classNames(
        {
          "uds-carousel-slide": [
            "products-carousel",
            "categories-carousel",
            "cards-list-carousel",
            "comparison-carousel",
            "brands-carousel",
          ].includes(variant),
          "banner-card--no-predefined-size": variant === "cards-list-carousel",
          "w-auto": variant === "simple-carousel",
          "uds-carousel-slide--trimmed": variant === "option-summary",
        },
        classes?.slide,
      ),
    [classes, variant],
  );

  const showPredefinedPrevIcon =
    !!carouselParams.navigation &&
    (carouselParams.navigation as NavigationOptions).prevEl ===
      ".js-carousel-prev";
  const showPredefinedNextIcon =
    !!carouselParams.navigation &&
    (carouselParams.navigation as NavigationOptions).nextEl ===
      ".js-carousel-next";

  const showPredefinedPagination =
    !!carouselParams.pagination &&
    (carouselParams.pagination as PaginationOptions).el === ".slide-number";

  const handleOnInstance = useCallback(
    (swiperInstance: SwiperClass) => {
      onInstance?.(swiperInstance);
    },
    [onInstance],
  );

  const circleArrows = (
    [
      "fullscreen",
      "brands-carousel",
      "categories-carousel",
      "products-carousel",
      "edit-product-details-carousel",
    ] as CarouselVariant[]
  ).includes(variant);

  const handleInit = useCallback(
    (swiper) => {
      if (swiper.params?.navigation || swiper.params?.pagination) {
        if (showPredefinedPrevIcon) {
          // eslint-disable-next-line no-param-reassign
          (swiper.params.navigation as NavigationOptions).prevEl =
            navigationPrevRef.current;
        }
        if (showPredefinedNextIcon) {
          // eslint-disable-next-line no-param-reassign
          (swiper.params.navigation as NavigationOptions).nextEl =
            navigationNextRef.current;
        }
        if (showPredefinedPagination) {
          // eslint-disable-next-line no-param-reassign
          (swiper.params.pagination as PaginationOptions).el =
            paginationRef.current;
        }
      }
    },
    [showPredefinedNextIcon, showPredefinedPagination, showPredefinedPrevIcon],
  );

  return (
    <Swiper
      data-testid="Carousel"
      onSwiper={handleOnInstance}
      onBeforeInit={handleInit}
      {...carouselParams}
    >
      {children.map((child) => (
        <SwiperSlide
          key={(child as ReactElement)?.props.id || (child as ReactElement).key}
          className={slideCommonClasses}
        >
          {child}
        </SwiperSlide>
      ))}

      {(
        [
          "fullscreen",
          "edit-product-details-carousel",
          "product-preview-carousel",
        ] as CarouselVariant[]
      ).includes(variant) ? (
        <div
          ref={paginationRef}
          className={
            variant === "fullscreen"
              ? "d-block product-image__counter"
              : "slide-number carousel-circle-pagination"
          }
        />
      ) : null}

      {showPredefinedPrevIcon ? (
        <div
          ref={navigationPrevRef}
          className={classNames("carousel-arrow-prev", {
            "carousel-arrow-prev--circle": circleArrows,
            "carousel-arrow-prev--pos-bottom-right": variant === "fullscreen",
          })}
        >
          <NavArrowNextSmallIcon />
        </div>
      ) : null}

      {showPredefinedNextIcon ? (
        <div
          ref={navigationNextRef}
          className={classNames("carousel-arrow-next", {
            "carousel-arrow-next--circle": circleArrows,
            "carousel-arrow-next--pos-bottom-right": variant === "fullscreen",
          })}
        >
          <NavArrowNextSmallIcon />
        </div>
      ) : null}
    </Swiper>
  );
};

export default Carousel;
